import React from 'react';
import cx from 'classnames';

import { withRouter, WithRouterProps } from 'react-router';
import Helmet from 'react-helmet';
import { makeStyles } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import { useIntl } from 'ha/i18n';
import { Grid, units } from '@ha/dsl';
import { checkSearchQueryEmpty } from 'ha/utils/checkSearchQueryEmpty';

import { PagerProps } from './types';
import PagerButton from './PagerButton';

const useStyles = makeStyles({
  indentTop: {
    paddingTop: units.rem(0.5),
  },
  indentBottom: {
    paddingBottom: units.rem(0.5),
  },
});

const Pager: React.FC<PagerProps & WithRouterProps> = ({
  pager,
  pagerData: {
    firstPage,
    lastPage,
    prevPage,
    nextPage,
    visiblePages,
    headCollapsed,
    tailCollapsed,
  },
  withHeadLinks = true,
  location: { pathname, search, query, state },
  router,
  indentTop = true,
  onPageChange,
}) => {
  const classes = useStyles();
  const { urlResolver } = useIntl();

  const getPageHref = React.useCallback(
    (pageNum: number, { isHeadLink = false } = {}) => {
      const { page, ...queryWithoutPage } = query;

      return router.createPath({
        pathname,
        search,
        state,
        query: {
          ...(!isHeadLink ? queryWithoutPage : {}),
          ...(pageNum !== 1
            ? {
                page: pageNum,
              }
            : {}),
        },
      });
    },
    [],
  );

  if (!pager.quantity || pager.total <= pager.quantity) {
    return null;
  }
  const isQueryEmpty = checkSearchQueryEmpty(query);

  return (
    <Grid
      container
      justify="flex-end"
      data-test-locator="Pagination Row"
      className={cx(classes.indentBottom, [{ [classes.indentTop]: indentTop }])}
    >
      {prevPage && (
        <PagerButton
          page={prevPage}
          getPageHref={getPageHref}
          onPageChange={onPageChange}
        >
          <ChevronLeft />
        </PagerButton>
      )}

      {prevPage && withHeadLinks && isQueryEmpty && (
        <Helmet>
          <link
            rel="prev"
            href={`${urlResolver.getBaseUrl()}${getPageHref(prevPage.num, {
              isHeadLink: true,
            })}`}
          />
        </Helmet>
      )}

      {headCollapsed && (
        <PagerButton
          page={firstPage}
          getPageHref={getPageHref}
          onPageChange={onPageChange}
        />
      )}

      {headCollapsed && <Button disabled>...</Button>}

      {visiblePages.map(page => (
        <PagerButton
          page={page}
          getPageHref={getPageHref}
          onPageChange={onPageChange}
          data-test-locator={`Page Button ${page.num}`}
          key={page.num}
        />
      ))}

      {tailCollapsed && <Button disabled>...</Button>}

      {tailCollapsed && (
        <PagerButton
          page={lastPage}
          getPageHref={getPageHref}
          onPageChange={onPageChange}
          data-test-locator="Last Page Button"
        />
      )}

      {nextPage && (
        <PagerButton
          page={nextPage}
          getPageHref={getPageHref}
          onPageChange={onPageChange}
          data-test-locator="Pager Next Page"
        >
          <ChevronRight />
        </PagerButton>
      )}

      {nextPage && withHeadLinks && isQueryEmpty && (
        <Helmet>
          <link
            rel="next"
            href={`${urlResolver.getBaseUrl()}${getPageHref(nextPage.num, {
              isHeadLink: true,
            })}`}
          />
        </Helmet>
      )}
    </Grid>
  );
};

export default withRouter(Pager);
